import { capitalize } from 'lodash';

import { PortalPaymentStopSetting, StopSettings } from '@willow/types-iso';

import { formatDate } from '../utils';

/**
 * Loose input shape that accepts:
 * - Full backend `StopSettings` with Date fields
 * - GraphQL-generated `StopSettings` with null-coalesced dates
 * - Any partial subset (e.g. the 3-field `LoanStatusStopSettingsInput`)
 */
export type StopSettingsTextInput = {
  portalPayments?: PortalPaymentStopSetting | string | null;
  emailsPaused?: boolean | null;
  documentAccessPaused?: boolean | null;
  drawsPaused?: boolean | null;
  advancesPaused?: boolean | null;
  advancesPausedUntilDate?: Date | string | null;
  omitFromPayoffAutomation?: boolean | null;
  omitFromDelinquentInterestAutomation?: boolean | null;
  omitFromLateFeeAutomation?: boolean | null;
  omitFromInterestOnEscrow?: boolean | null;
  miCancellationReviewRequired?: boolean | null;
  miCancellationReviewReason?: string | null;
};

// some messages have an input check, so regular Record type won't work
type StopSettingsMessages = {
  [K in keyof Required<
    Omit<StopSettings, 'advancesPausedUntilDate' | 'miCancellationReviewReason'>
  >]: K extends 'portalPayments'
    ? { portalPaymentsNone: string; portalPaymentsExact: string }
    : K extends 'advancesPaused'
      ? (date?: Date) => string
      : K extends 'miCancellationReviewRequired'
        ? (reason?: string) => string
        : string;
};

export const getStopSettingsTextFromStopSettings = (stopSettings: StopSettingsTextInput): string => {
  const restrictedMessages: StopSettingsMessages = {
    emailsPaused: 'borrower notifications are paused',
    portalPayments: {
      portalPaymentsNone: 'payments are not accepted', // Note: the language around flagging/stop settings is "portal", but currently its applied to both portal and lender payment recordings
      portalPaymentsExact: 'incomplete payments are not accepted',
    },
    documentAccessPaused: 'borrower document access is limited',
    drawsPaused: 'ability to record draws is paused',
    advancesPaused: (date?: Date) =>
      date
        ? `ability to record advances is paused until ${formatDate(date, 'M/d/yy')}`
        : 'ability to record advances is paused',
    omitFromPayoffAutomation: 'loan is omitted from payoff automation',
    omitFromDelinquentInterestAutomation: 'loan is omitted from default interest automation',
    omitFromLateFeeAutomation: 'loan is omitted from late fee automation',
    omitFromInterestOnEscrow: 'loan is omitted from interest on escrow report',
    miCancellationReviewRequired: (reason?: string) =>
      reason
        ? `manual mortgage insurance cancellation review required: ${reason}`
        : 'manual mortgage insurance cancellation review required',
  } as const;
  const {
    emailsPaused,
    portalPayments,
    documentAccessPaused,
    drawsPaused,
    advancesPaused,
    advancesPausedUntilDate,
    omitFromPayoffAutomation,
    omitFromDelinquentInterestAutomation,
    omitFromLateFeeAutomation,
    omitFromInterestOnEscrow,
    miCancellationReviewRequired,
    miCancellationReviewReason,
  } = stopSettings;

  const messages: string[] = [];
  if (emailsPaused) {
    messages.push(restrictedMessages.emailsPaused);
  }

  if (portalPayments === 'none') {
    messages.push(restrictedMessages.portalPayments.portalPaymentsNone);
  }

  if (portalPayments === 'exact') {
    messages.push(restrictedMessages.portalPayments.portalPaymentsExact);
  }

  if (documentAccessPaused) {
    messages.push(restrictedMessages.documentAccessPaused);
  }

  if (drawsPaused) {
    messages.push(restrictedMessages.drawsPaused);
  }

  if (advancesPaused) {
    const pausedUntilDate =
      advancesPausedUntilDate instanceof Date
        ? advancesPausedUntilDate
        : typeof advancesPausedUntilDate === 'string'
          ? new Date(advancesPausedUntilDate)
          : undefined;
    messages.push(restrictedMessages.advancesPaused(pausedUntilDate));
  }

  if (omitFromPayoffAutomation) {
    messages.push(restrictedMessages.omitFromPayoffAutomation);
  }

  if (omitFromDelinquentInterestAutomation) {
    messages.push(restrictedMessages.omitFromDelinquentInterestAutomation);
  }

  if (omitFromLateFeeAutomation) {
    messages.push(restrictedMessages.omitFromLateFeeAutomation);
  }

  if (omitFromInterestOnEscrow) {
    messages.push(restrictedMessages.omitFromInterestOnEscrow);
  }

  if (miCancellationReviewRequired) {
    messages.push(restrictedMessages.miCancellationReviewRequired(miCancellationReviewReason ?? undefined));
  }

  return messages.length === 0 ? '' : capitalize(messages.join(', '));
};
